import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import { useListContext } from './list-context'
import type { ListSubheaderProps } from './types'
import { LIST_THEME_KEY } from './types'
import { createUseStyledComponent } from '../../hooks'
import { __DEV__ } from '../../utils/assertion'

const useStyledComponent = createUseStyledComponent(
  ['li'],
  'csg-list-subheader'
)

const ListSubheaderPrivate: FC<ListSubheaderProps> = React.forwardRef<
  HTMLLIElement,
  ListSubheaderProps
>(function ListSubheaderPrivate(props, ref) {
  const {
    variant = 'listSubheader',
    className,
    color = 'neutral.dark',
    lineHeight = 2,
    as: component = 'li',
    disableGutters = false,
    disableSticky = false,
    inset = false,
    ...rest
  } = props
  const { dense, variant: listVariant } = useListContext()
  const Component = useStyledComponent(component)
  const __css = useMemo(
    () => ({
      color,
      ...(lineHeight ? { lineHeight } : {}),
      ...(inset
        ? {
            pl: '72px'
          }
        : {}),
      ...(!disableSticky
        ? {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'white'
          }
        : {}),
      ...(!disableGutters ? { px: dense ? 'xs' : 'md' } : {})
    }),
    [color, dense, disableGutters, disableSticky, inset, lineHeight]
  )
  return (
    <Component
      // until there are use case where this component is used in the a compound component we keep it simple
      variant={listVariant ? `${listVariant}.listSubheader` : variant}
      __themeKey={LIST_THEME_KEY}
      ref={ref}
      className={classNames('csg-tui-list-subheader', className)}
      __css={__css}
      {...rest}
    />
  )
})

const MemoListSubheader = React.memo(ListSubheaderPrivate)

const ListSubheader: FC<ListSubheaderProps> = React.forwardRef<
  HTMLLIElement,
  ListSubheaderProps
>((props, ref) => <MemoListSubheader ref={ref} {...props} />)

if (__DEV__) {
  ListSubheader.displayName = 'ListSubheader'
}

export default ListSubheader
